import { useQuery } from "@tanstack/react-query";
import { Info, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useSearchParams } from "react-router";
import { useSession } from "../admin/session.ts";
import { api } from "../api/client.ts";
import { safeNext } from "../auth/next.ts";
import { useLocale } from "../i18n/index.ts";
import { AuthCard } from "./auth/AuthCard.tsx";
import { landingFor, nextSearch } from "./auth/landing.ts";
import { SignInForm } from "./auth/SignInForm.tsx";

const NOTICE_KEY = "divespots.loginPasswordNotice";

const noticeDismissed = (): boolean => {
  try {
    return localStorage.getItem(NOTICE_KEY) === "1";
  } catch {
    return false;
  }
};

/** Where the visitor was going: `?next=` when it is a path on this site, else nothing. (Older links carried it in the router state.) */
export function useNext(): string | null {
  const location = useLocation();
  const [params] = useSearchParams();
  const stateFrom = (location.state as { from?: string } | null)?.from;
  return safeNext(params.get("next")) ?? safeNext(stateFrom);
}

/** GET /api/config: false when registration is closed. While unknown, the link is hidden. */
export function useRegistrationOpen(): { known: boolean; open: boolean } {
  const config = useQuery({
    queryKey: ["config"],
    queryFn: ({ signal }) => api.publicConfig(signal),
    staleTime: 60_000,
    retry: false,
  });
  return { known: config.data !== undefined, open: config.data?.registrationOpen === true };
}

/**
 * The one login for everybody, staff included: passkey, then email + password. A one-time notice
 * tells existing divers (who had no password) to set one with the reset flow.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const session = useSession();
  const [params] = useSearchParams();
  const next = useNext();
  const reg = useRegistrationOpen();
  const [landed, setLanded] = useState<string | null>(null);
  const [notice, setNotice] = useState(() => !noticeDismissed());

  // links of the old magic-link login (?mode=register) keep working
  if (params.get("mode") === "register") {
    return <Navigate to={`/${locale}/login/registrieren${nextSearch(next)}`} replace />;
  }
  if (session.status === "user") {
    return <Navigate to={landed ?? landingFor(session.user, next, locale)} replace />;
  }

  const dismiss = () => {
    try {
      localStorage.setItem(NOTICE_KEY, "1");
    } catch {
      /* not remembered */
    }
    setNotice(false);
  };

  return (
    <AuthCard title={t("diver.login.title")} lead={t("diver.login.lead")}>
      {notice ? (
        <aside className="login-notice" aria-label={t("diver.login.noticeLabel")}>
          <Info aria-hidden="true" size={20} strokeWidth={1.75} />
          <p className="ds-text-subheadline m-0">
            {t("diver.login.noticeText")}{" "}
            <Link to={`/${locale}/login/passwort-vergessen${nextSearch(next)}`}>
              {t("diver.login.noticeLink")}
            </Link>
          </p>
          <button
            type="button"
            className="ds-btn ds-btn--plain ds-btn--icon"
            aria-label={t("diver.login.noticeDismiss")}
            onClick={dismiss}
          >
            <X aria-hidden="true" strokeWidth={1.75} />
          </button>
        </aside>
      ) : null}
      {reg.known && !reg.open ? (
        <p className="adm-warning" role="status" data-testid="registration-closed">
          <Info aria-hidden="true" size={20} strokeWidth={1.75} />
          {t("diver.login.registrationClosed")}
        </p>
      ) : null}
      <SignInForm
        locale={locale}
        search={nextSearch(next)}
        registrationOpen={reg.open}
        onSignedIn={(user) => setLanded(landingFor(user, next, locale))}
      />
    </AuthCard>
  );
}
